import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Check, GripVertical, Plus, X } from 'lucide-react';
import api from '@/services/apiClient';
import useAuthStore from '@/store/authStore';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import ImageUpload from '@/components/ui/ImageUpload';
import Avatar from '@/components/ui/Avatar';
import { fmtMoney } from '@/lib/enums';
import { maskMoney, moneyToNumber, numberToMoney } from '@/lib/masks';
import styles from './PackageForm.module.css';

const DEFAULT_COMMISSION = 0.15;

const CATEGORY_VALUES = ['hipertrofia', 'emagrecimento', 'forca', 'condicionamento', 'funcional', 'saude'];
const LEVEL_VALUES = ['iniciante', 'intermediario', 'avancado'];

const daysToWeeks = (d) => (d ? Math.round(Number(d) / 7) : '');
const weeksToDays = (w) => (w ? Math.round(Number(w) * 7) : null);

export default function PackageForm({ initial, onSubmit, onCancel }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const categoryOptions = CATEGORY_VALUES.map((value) => ({ value, label: t(`packages.categories.${value}`) }));
  const levelOptions = LEVEL_VALUES.map((value) => ({ value, label: t(`packages.levels.${value}`) }));
  const [form, setForm] = useState({
    title: initial?.title || '',
    description: initial?.description || '',
    price: numberToMoney(initial?.price),
    coverUrl: initial?.coverUrl || '',
    category: initial?.category || '',
    weeks: daysToWeeks(initial?.durationDays),
    level: initial?.level || '',
  });
  // Conteúdo incluído é só presentacional (a API não persiste a lista).
  const [contents, setContents] = useState([t('packages.form.contentExample')]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [commissionPct, setCommissionPct] = useState(DEFAULT_COMMISSION);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Comissão vigente da plataforma (configurável pelo admin).
  useEffect(() => {
    api
      .get('/billing/commission')
      .then((r) => {
        const pct = Number((r.data || r).commissionPct);
        if (Number.isFinite(pct)) setCommissionPct(pct);
      })
      .catch(() => {});
  }, []);

  const price = moneyToNumber(form.price) || 0;
  const commission = useMemo(() => price * commissionPct, [price, commissionPct]);
  const net = price - commission;

  const updateContent = (i, v) => setContents((c) => c.map((x, idx) => (idx === i ? v : x)));
  const removeContent = (i) => setContents((c) => c.filter((_, idx) => idx !== i));
  const addContent = () => setContents((c) => [...c, '']);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setErr(t('packages.form.titleRequired'));
    setSaving(true);
    setErr(null);
    try {
      await onSubmit({
        title: form.title,
        description: form.description || null,
        coverUrl: form.coverUrl || null,
        price,
        category: form.category || null,
        durationDays: weeksToDays(form.weeks),
      });
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSaving(false);
    }
  };

  const weeksLabel = form.weeks ? t('packages.form.weeksValue', { count: Number(form.weeks) }) : t('packages.form.weeksFallback');

  return (
    <form onSubmit={submit} className={styles.wrap}>
      {/* ---------- coluna do formulário ---------- */}
      <div className={styles.formCol}>
        <div>
          <span className={styles.fld}>{t('packages.form.cover')}</span>
          <ImageUpload
            folder="covers"
            value={form.coverUrl}
            onChange={(url) => setForm((f) => ({ ...f, coverUrl: url }))}
          />
        </div>

        <div className={styles.row2}>
          <Input label={t('packages.form.title')} value={form.title} onChange={set('title')} placeholder={t('packages.form.titlePlaceholder')} />
          <Input label={t('packages.form.weeks')} type="number" min="1" value={form.weeks} onChange={set('weeks')} placeholder="12" />
        </div>

        <Textarea label={t('packages.form.description')} value={form.description} onChange={set('description')} rows={3} placeholder={t('packages.form.descriptionPlaceholder')} />

        <div className={styles.rowEven}>
          <Select label={t('packages.form.category')} value={form.category} onChange={set('category')} placeholder={t('packages.form.selectPlaceholder')} options={categoryOptions} />
          <Select label={t('packages.form.level')} value={form.level} onChange={set('level')} placeholder={t('packages.form.selectPlaceholder')} options={levelOptions} />
        </div>

        <div>
          <span className={styles.fld}>{t('packages.form.includedContent')}</span>
          <div className={styles.contentList}>
            {contents.map((c, i) => (
              <div key={i} className={styles.contentItem}>
                <span className={styles.check}><Check size={13} strokeWidth={2.5} /></span>
                <input value={c} onChange={(e) => updateContent(i, e.target.value)} placeholder={t('packages.form.contentExample')} />
                <span className={styles.handle} aria-hidden><GripVertical size={16} strokeWidth={2} /></span>
                <button type="button" className={styles.rmContent} onClick={() => removeContent(i)} aria-label={t('common.delete')}>
                  <X size={14} strokeWidth={2} />
                </button>
              </div>
            ))}
            <button type="button" className={styles.addContent} onClick={addContent}>
              <Plus size={16} strokeWidth={2} /> {t('packages.form.addContent')}
            </button>
          </div>
        </div>
      </div>

      {/* ---------- coluna de preço + prévia ---------- */}
      <div className={styles.previewCol}>
        <div className={styles.priceRow}>
          <Input className={styles.bigInput} label={t('packages.form.price')} inputMode="numeric" value={form.price}
            onChange={(e) => setForm((f) => ({ ...f, price: maskMoney(e.target.value) }))} placeholder="49,90" />
          <div>
            <span className={styles.fld}>{t('packages.stats.youReceive')}</span>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22, color: 'var(--volt)', marginTop: 8 }}>
              {fmtMoney(net)}
            </div>
          </div>
        </div>

        <div className={styles.commission}>
          <Trans
            i18nKey="packages.form.commissionNote"
            values={{ amount: fmtMoney(commission), pct: Math.round(commissionPct * 100) }}
            components={{ strong: <strong /> }}
          />
        </div>

        <div>
          <span className={styles.fld}>{t('packages.form.previewLabel')}</span>
          <div className={styles.card}>
            <div
              className={styles.cardCover}
              style={form.coverUrl ? { backgroundImage: `url(${form.coverUrl})` } : undefined}
            >
              {form.weeks && <span className={styles.cardBadge}>{weeksLabel}</span>}
            </div>
            <div className={styles.cardBody}>
              <div className={styles.cardTitle}>{form.title || t('packages.form.previewTitleFallback')}</div>
              <div className={styles.cardAuthor}>
                <Avatar name={user?.name} src={user?.avatar} size={20} />
                {t('packages.form.previewAuthor', { name: user?.name || t('packages.form.defaultAuthor') })}
              </div>
              <div className={styles.cardFoot}>
                <span className={styles.cardPrice}>{fmtMoney(price)}</span>
                <span className={styles.cardBuy}>{t('packages.form.buy')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {err && <span className={styles.error} style={{ gridColumn: '1 / -1' }}>{err}</span>}
      <div className={styles.footer}>
        <Button type="button" variant="ghost" onClick={onCancel}>{t('packages.form.saveDraft')}</Button>
        <Button type="submit" loading={saving}>{t('packages.form.publish')}</Button>
      </div>
    </form>
  );
}
